// Phases, and the seeded plan that makes runs differ.
//
// The central structural decision of the rebuild lives here. Within a
// phase the spell-card sequence is **fixed and learnable** — that is
// what makes mastery possible, and it is the fix for the old system's
// weighted-dice attack selection, which no amount of practice could
// ever beat. Across runs, *which* sequence you get is drawn by seed
// from several candidate sets per phase.
//
// So: Punch-Out learnability inside a run, roguelite surprise between
// runs, from one mechanism.

import { clamp, cosT, sinT, xmur3, type Rng } from "./rng";
import { patternById } from "./patterns";
import { ARENA_H, ARENA_W, type Phase, type SimState, type SpellCard } from "./types";

/**
 * A spell card. `pattern` is a pattern **id**, and the display name is
 * taken from the pattern itself rather than retyped here — a card that
 * announces "SIGPIPE Cascade" and then fires something else is a lie
 * the player can see, and duplicating the name invites exactly that.
 */
const card = (patternId: string, hpShare: number, seconds: number): SpellCard => {
  const p = patternById(patternId);
  if (!p) throw new Error(`phases: unknown pattern id "${patternId}"`);
  return {
    id: patternId,
    name: p.name,
    pattern: patternId,
    hpShare,
    timeout: Math.round(seconds * 60),
  };
};

/**
 * The five phases. Each introduces one *device* — a new rule — rather
 * than a bigger number, because rule changes are memorable and
 * multipliers are not.
 *
 * `nice` counts down as DAEMON renices itself out of your reach, which
 * makes the difficulty curve diegetic instead of a tuning table.
 */
export const PHASES: Phase[] = [
  {
    id: "rl5",
    nice: 19,
    name: "runlevel 5 — multi-user",
    device: "none",
    // Every set opens on "hello". The first card is the tutorial — it
    // teaches dodging, then grazing, then signals — and a tutorial you
    // cannot rely on teaches nothing. Variety starts at card two.
    cardSets: [
      [card("hello", 0.45, 22), card("sigpipe", 0.55, 26)],
      [card("hello", 0.45, 22), card("polling", 0.55, 26)],
      [card("hello", 0.45, 22), card("broadcast", 0.55, 26)],
      [card("hello", 0.45, 22), card("logrotate", 0.55, 26)],
      [card("hello", 0.45, 22), card("blocking", 0.55, 26)],
    ],
  },
  {
    id: "rl4",
    nice: 10,
    name: "runlevel 4 — handlers installed",
    device: "handlers",
    cardSets: [
      [card("roundrobin", 0.5, 26), card("keepalive", 0.5, 26)],
      [card("nightly", 0.55, 28), card("priority", 0.45, 24)],
      [card("reaper", 0.5, 26), card("orphans", 0.5, 28)],
      [card("priority", 0.45, 24), card("nightly", 0.55, 28)],
      [card("keepalive", 0.5, 26), card("reaper", 0.5, 26)],
    ],
  },
  {
    id: "rl3",
    nice: 0,
    name: "runlevel 3 — fork bomb",
    device: "fork",
    cardSets: [
      [card("forkbomb", 0.55, 30), card("dmesg", 0.45, 26)],
      [card("retransmit", 0.5, 28), card("spinlock", 0.5, 28)],
      [card("bouncebuf", 0.5, 28), card("coredump", 0.5, 30)],
      [card("spinlock", 0.5, 28), card("forkbomb", 0.5, 30)],
      [card("coredump", 0.55, 30), card("retransmit", 0.45, 26)],
    ],
  },
  {
    id: "rl2",
    nice: -10,
    name: "runlevel 2 — swap thrash",
    device: "shrink",
    cardSets: [
      [card("oomkiller", 0.55, 30), card("racecond", 0.45, 26)],
      [card("tlbshoot", 0.5, 28), card("writebarrier", 0.5, 28)],
      [card("deadlock", 0.5, 28), card("irqstorm", 0.5, 30)],
      [card("irqstorm", 0.5, 30), card("tlbshoot", 0.5, 28)],
      [card("writebarrier", 0.45, 26), card("oomkiller", 0.55, 30)],
    ],
  },
  {
    id: "rl1",
    nice: -20,
    name: "runlevel 1 — single-user",
    device: "single-user",
    cardSets: [
      [card("dstate", 0.55, 32), card("initdead", 0.45, 28)],
      [card("sig9", 0.5, 30), card("panic", 0.5, 30)],
      [card("panic", 0.5, 30), card("initdead", 0.5, 28)],
      [card("dstate", 0.5, 32), card("sig9", 0.5, 30)],
    ],
  },
];

export function planCards(rng: Rng): SpellCard[][] {
  return PHASES.map((p) => rng.pick(p.cardSets));
}

export const phaseAt = (i: number): Phase => PHASES[Math.min(i, PHASES.length - 1)]!;

// ── Devices ───────────────────────────────────────────────────────────
//
// Everything below is the *rule* half of a phase: the tuning, the
// schedules and the geometry each device needs. It lives here rather
// than in sim.ts so that the sim stays a wiring diagram and a designer
// can retune the fight without reading a single line of the integrator.
//
// Nothing here reads a clock or a random source it wasn't handed. Every
// function is either pure arithmetic on the tick counter or a draw from
// the caller's seeded `Rng`, which is what keeps replays honest.

/** Device tuning. Ticks throughout — 60 to the second. */
export const DEVICE = {
  /** runlevel 4. A metronome, deliberately: the whole point of the
   *  device is that the window is *learnable*, so it must be a beat you
   *  can count rather than a die-roll you can only react to. */
  handler: {
    /** Ticks between window openings. 7s. */
    period: 420,
    /** Ticks into each period at which the window opens. The offset
     *  exists so a phase never opens on a guard before the player has
     *  seen DAEMON at all. */
    openAt: 180,
    /** How long SIGTERM bounces. 2.2s — long enough to force a decision
     *  about what to do instead, short enough not to be a dead zone. */
    window: 132,
    /** Radius of the telegraph ring drawn around DAEMON. */
    telegraphR: 130,
  },

  /** runlevel 3. */
  fork: {
    /** Phase-clock tick of the first fork, then every `period`. */
    first: 240,
    period: 900,
    /** Children per fork, and the ceiling on live children. */
    count: 2,
    max: 4,
    /** A child dies to two SIGKILLs' worth of nothing — ten SIGTERMs,
     *  or one SIGKILL you would rather have spent on the parent. */
    hp: 60,
    /** Ticks between a child's emissions. Slower than DAEMON: children
     *  are pressure, not a second boss. */
    emitEvery: 84,
    /** Bullets a child may emit in one step, and their damage. */
    maxCount: 3,
    damage: 6,
    speedScale: 0.9,
    /** HP per tick DAEMON regains for each live child. This is the
     *  other half of the trade: ignore the children and they shoot you,
     *  clear the children and the parent undoes your work. */
    healPerChild: 0.05,
    /** Cursor distance within which a signal retargets from the parent
     *  to the nearest child. Being able to hit a child means standing
     *  next to it, which is the cost. */
    reach: 220,
  },

  /** The corpse a killed child leaves. */
  zombie: {
    /** Ticks of dwell to reap. 1.6s of standing still, near the fight. */
    reap: 96,
    /** Cursor distance that counts as dwelling. */
    range: 96,
    /** PRIV paid out for a reap. The greed: options, in exchange for
     *  having stood in the worst place on the board for a second and a
     *  half. */
    priv: 3,
    /** Ticks per point of dwell lost when you step away. Leaving is
     *  cheaper than arriving, but not free. */
    decayEvery: 2,
  },

  /** runlevel 2 — the arena closes in. */
  shrink: {
    /** Floor. Below this a cursor genuinely cannot survive a ring, and
     *  "unsurvivable" is not a difficulty setting. */
    min: 0.62,
    /** Where the arena settles for runlevel 1. init gets the console
     *  back, a bit — single-user is the cursor-only test, not a coffin. */
    rest: 0.8,
    /** Scale change per tick. Slow enough to feel like a wall moving
     *  rather than a teleport. */
    rate: 0.0015,
    /** Quantisation for the `shrink` event, so the host animates in
     *  steps instead of receiving 60 events a second. */
    step: 0.02,
  },

  /** runlevel 1. Parked into every cooldown so any HUD that renders
   *  "ticks remaining" renders the signal as unavailable without
   *  needing to know the device exists. */
  singleUser: { lock: 99999 },
} as const;

/**
 * Which devices are live at a given phase index.
 *
 * Devices **accumulate**. A rule DAEMON has demonstrated does not get
 * politely withdrawn next phase; that is what makes the fight feel like
 * it is closing rather than cycling. The two exceptions are deliberate:
 * handlers stop mattering at runlevel 1 (there are no signals to guard
 * against), and forking stops there too — init reaps everything on the
 * way down, which is both true to the metaphor and the only reason the
 * last phase is survivable with no verbs.
 */
export function deviceOn(phaseIndex: number, device: Phase["device"]): boolean {
  switch (device) {
    case "handlers":
      return phaseIndex >= 1 && phaseIndex <= 3;
    case "fork":
      return phaseIndex >= 2 && phaseIndex <= 3;
    case "shrink":
      return phaseIndex >= 3;
    case "single-user":
      return phaseIndex >= 4;
    default:
      return false;
  }
}

type ClockState = Pick<SimState, "cardPlan" | "phaseIndex" | "cardIndex" | "cardTicks">;

/**
 * Ticks elapsed in the current phase, derived rather than stored.
 *
 * Card timers already encode this: everything before the current card
 * ran to its timeout or was cut short, and the current card has burned
 * `timeout - cardTicks`. Deriving it keeps `SimState` from growing a
 * field that could disagree with the cards, and it means the clock a
 * device schedules against is the same clock the player can see on the
 * card timer.
 *
 * Monotonic, and it can jump *forward* when a card is cleared on HP
 * rather than time — which is why every schedule below is written as an
 * edge crossing rather than an equality test.
 */
export function phaseClock(s: ClockState): number {
  const cards = s.cardPlan[s.phaseIndex] ?? [];
  let t = 0;
  for (let i = 0; i < s.cardIndex && i < cards.length; i++) t += cards[i]!.timeout;
  const current = cards[s.cardIndex];
  if (current) t += Math.max(0, current.timeout - s.cardTicks);
  return t;
}

/** Total authored length of a phase, in ticks. */
export function phaseLength(s: ClockState): number {
  const cards = s.cardPlan[s.phaseIndex] ?? [];
  let t = 0;
  for (const c of cards) t += c.timeout;
  return t;
}

/** 0 at the start of a phase, 1 at its authored end. */
export function phaseProgress(s: ClockState): number {
  const len = phaseLength(s);
  return len <= 0 ? 1 : clamp(phaseClock(s) / len, 0, 1);
}

/** Is the handler window open at this point on the phase clock? */
export function handlerOpen(clock: number): boolean {
  const { period, openAt, window } = DEVICE.handler;
  if (clock < openAt) return false;
  return (clock - openAt) % period < window;
}

// ── The arena ─────────────────────────────────────────────────────────

export type ArenaBounds = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  w: number;
  h: number;
};

/** The live playfield for a given scale, centred in the logical arena.
 *  Everything that has a position gets clamped into this every tick. */
export function arenaBounds(scale: number): ArenaBounds {
  const s = clamp(scale, DEVICE.shrink.min, 1);
  const w = ARENA_W * s;
  const h = ARENA_H * s;
  const x0 = (ARENA_W - w) / 2;
  const y0 = (ARENA_H - h) / 2;
  return { x0, y0, x1: x0 + w, y1: y0 + h, w, h };
}

/**
 * Where the arena wants to be, given the phase and how far into it we
 * are. The sim eases toward this rather than snapping, so the wall
 * moves at a speed a human can plan around.
 */
export function arenaTarget(phaseIndex: number, progress: number): number {
  if (deviceOn(phaseIndex, "single-user")) return DEVICE.shrink.rest;
  if (deviceOn(phaseIndex, "shrink")) {
    return 1 - (1 - DEVICE.shrink.min) * clamp(progress, 0, 1);
  }
  return 1;
}

// ── DAEMON's patrol ───────────────────────────────────────────────────
//
// Patterns are aimed from DAEMON's position, so a stationary DAEMON
// makes every card read the same way twice. It moves — but on a fixed
// closed loop drawn from the seed, not a wander. That distinction is
// the whole design: across runs the loop differs, within a run you can
// learn it, and "learn where it will be" is a skill a mouse can express.

type PathParams = { xPeriod: number; yPeriod: number; xPhase: number; yPhase: number };

/** Loop lengths in ticks. All several seconds long, so the patrol reads
 *  as a sweep rather than a jitter, and none of them share a factor
 *  with the handler metronome — a DAEMON that was always in the same
 *  place when it guarded would collapse two tells into one. */
const X_PERIODS = [640, 758, 874, 1006];
const Y_PERIODS = [402, 486, 558];

/** Seed → patrol shape. Memoised because it is a pure function of the
 *  seed string; the cache is an optimisation, never state. */
const PATH_CACHE = new Map<string, PathParams>();

export function daemonPath(seed: string): PathParams {
  const cached = PATH_CACHE.get(seed);
  if (cached) return cached;
  // A distinct hash stream from the one that draws the card plan, so
  // patrol and playlist vary independently across seeds.
  const h = xmur3(`patrol:${seed}`);
  const params: PathParams = {
    xPeriod: X_PERIODS[h() % X_PERIODS.length]!,
    yPeriod: Y_PERIODS[h() % Y_PERIODS.length]!,
    xPhase: (h() % 1024) / 1024,
    yPhase: (h() % 1024) / 1024,
  };
  PATH_CACHE.set(seed, params);
  return params;
}

/**
 * DAEMON's position at a tick. A Lissajous figure across the top band
 * of the live arena, widening as the runlevel drops.
 *
 * Pure in `(seed, tick, phaseIndex, bounds)` — it consumes no
 * randomness at all, which means it costs a replay nothing and a
 * player can genuinely memorise it.
 */
export function daemonPos(
  seed: string,
  tick: number,
  phaseIndex: number,
  b: ArenaBounds,
): { x: number; y: number } {
  const p = daemonPath(seed);
  // Peak speed at the widest setting, both axes at once, is under 2.7
  // units/tick — a shade below the slowest bullets. DAEMON drifts; it
  // does not dart. Widen these and it starts outrunning its own
  // patterns, which reads as teleporting rather than moving.
  const ampX = b.w * (0.14 + 0.025 * Math.min(phaseIndex, 4));
  const ampY = b.h * (0.03 + 0.012 * Math.min(phaseIndex, 4));
  const cx = b.x0 + b.w / 2;
  const cy = b.y0 + b.h * 0.26;
  const x = cx + cosT(tick / p.xPeriod + p.xPhase) * ampX;
  const y = cy + sinT(tick / p.yPeriod + p.yPhase) * ampY;
  const inset = Math.min(60, b.w * 0.08);
  return {
    x: clamp(x, b.x0 + inset, b.x1 - inset),
    // Kept in the upper half. A boss that can stand on top of you is a
    // different, worse game.
    y: clamp(y, b.y0 + inset, b.y0 + b.h * 0.5),
  };
}

/**
 * Where a fork's children land, as arena-absolute points.
 *
 * Absolute rather than relative to DAEMON on purpose: the spawn is
 * telegraphed half a second ahead and DAEMON is moving, so a relative
 * slot would put the child somewhere the warning never pointed at.
 */
export function forkSlots(b: ArenaBounds, count: number, rng: Rng): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let k = 0; k < count; k++) {
    const t = (k + 1) / (count + 1);
    const jitter = rng.range(-0.07, 0.07);
    out.push({
      x: b.x0 + b.w * clamp(t + jitter, 0.14, 0.86),
      // Below DAEMON's band and above the player's usual station — the
      // children sit in the middle of the board, in the way.
      y: b.y0 + b.h * rng.range(0.36, 0.52),
    });
  }
  return out;
}
